import { action, makeObservable, observable } from "mobx";
import ListStore from "./_listStore";
import { IStatus } from "../components/status/status";
import { IPropertyItem } from "./store";

export interface ISubscriptionItem {
    id: number,
    city: {
        id: number,
        name: string,
    },
    expire: number,
    expire_formatted: string,
    customer: {
        id: number,
        name: string,
        lastName: string,
        email: string,
        phone: string,
    },
    count: number,
    status: IStatus,
    product: {
        id: number,
        name: string,
    },
}

export interface ISubscriptionPurchaseItem {
    id: number,
    date: string,
    product_name: string,
    count: number,
    total: number,
    status: IStatus,
}

export interface ISubscriptionGameItem {
    id: number,
    date: string,
    place_name: string,
    count: number,
    status: IStatus,
}

export interface ISubscriptionItemDetail extends Omit<ISubscriptionItem, 'product'> {
    created: number,
    product: {
        id: number,
        name: string,
        count: number,
        period_use_days: number,
        price: number,
    },
    purchases: ISubscriptionPurchaseItem[],
    games: ISubscriptionGameItem[],
}

export interface ISubscriptionItemDetailError {
    count?: string,
    expire?: string,
}

export interface ISubscriptionFilter {
    name: string,
    email: string,
    phone: string,
    status: number | null,
}

export type ISubscriptionSortField = 'id' | 'status' | 'count' | 'expire';

export interface ISubscriptionSort {
    field: ISubscriptionSortField,
    order: 'asc' | 'desc',
}

const defaultFilter: ISubscriptionFilter = {
    name: '',
    email: '',
    phone: '',
    status: null,
}

interface ISubscriptionsStore {
    items: ISubscriptionItem[],
}

class _SubscriptionsStore extends ListStore implements ISubscriptionsStore {
    items = [] as ISubscriptionItem[]
    filter = { ...defaultFilter } as ISubscriptionFilter
    sort = { field: 'id', order: 'desc' } as ISubscriptionSort
    statuses = [] as IPropertyItem[]
    editItem = {} as ISubscriptionItemDetail
    editItemError = {} as ISubscriptionItemDetailError

    constructor() {
        super();
        this.name = '__subscriptions__'
        makeObservable(this, {
            items: observable,
            filter: observable,
            sort: observable,
            statuses: observable,
            editItem: observable,
            editItemError: observable,
            setItems: action,
            setFilterOptions: action,
            setFilter: action,
            resetFilter: action,
            setSort: action,
            setEditItemFull: action,
            setEditItem: action,
            setEditItemError: action,
        })
    }
    setItems = (items: ISubscriptionItem[]) => {
        this.items = items;
    }
    setFilterOptions = (filter: { statuses: { id: number, name: string }[] }) => {
        this.statuses = (filter.statuses || []).map(status => ({ id: status.id, title: status.name }))
    }
    setFilter = <K extends keyof ISubscriptionFilter>(field: K, value: ISubscriptionFilter[K]) => {
        this.filter[field] = value
    }
    resetFilter = () => {
        this.filter = { ...defaultFilter }
    }
    setSort = (field: ISubscriptionSortField) => {
        if (this.sort.field === field) {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc'
        } else {
            this.sort.field = field
            this.sort.order = 'asc'
        }
    }
    setEditItemFull = (item: ISubscriptionItemDetail) => {
        this.editItem = item
    }
    setEditItem = <K extends keyof ISubscriptionItemDetail>(
        field: K,
        value: ISubscriptionItemDetail[K]) => {
        this.editItem[field] = value
    }
    setEditItemError = (item?: ISubscriptionItemDetailError) => {
        this.editItemError = item || {} as ISubscriptionItemDetailError
    }
    getListUrl = () => {
        let params = new URLSearchParams();
        params.set('limit', String(this.pagination.limit))
        params.set('page', String(this.pagination.page))
        params.set('sort', this.sort.field)
        params.set('order', this.sort.order)
        if (this.filter.name) params.set('name', this.filter.name)
        if (this.filter.email) params.set('email', this.filter.email)
        if (this.filter.phone) params.set('phone', this.filter.phone)
        if (this.filter.status !== null && this.filter.status !== undefined) params.set('status', String(this.filter.status))
        return params.toString()
    }
}

const SubscriptionsStore = new _SubscriptionsStore()

export default SubscriptionsStore;
